"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CREATIVE_METRIC_BY_KEY = exports.CREATIVE_METRIC_KEYS = exports.CREATIVE_METRIC_ENTRIES = exports.CREATIVE_METRIC_CATALOG = exports.ANNOTATION_STATUS_LABEL = exports.ANNOTATION_STATUSES = exports.CREATIVE_METRIC_GROUP_META = exports.CREATIVE_METRIC_GROUPS = void 0;
exports.isNumericCreativeMetric = isNumericCreativeMetric;
exports.creativeMetricsInGroup = creativeMetricsInGroup;
exports.creativeMetric = creativeMetric;
exports.isCreativeMetricKey = isCreativeMetricKey;
/**
 * The funnel groups, in the order every surface presents them: outcome first, diagnostics last.
 *
 * The order IS the doctrine — a decision metric and a diagnostic metric answer different questions,
 * and a picker that interleaves them invites reading a hook rate as a verdict. The two
 * business-model groups sit after the shared diagnostics because they activate on what the account
 * measures rather than being universal.
 */
exports.CREATIVE_METRIC_GROUPS = [
    'results',
    'delivery',
    'clicks',
    'funnel',
    'video',
    'engagement',
    'leads',
    'appTrials',
    'lifecycle',
];
/** How a group names itself, and the one line stating what it is for. */
exports.CREATIVE_METRIC_GROUP_META = {
    results: {
        label: 'Results',
        description: 'Did money in become money out? These judge the creative.',
    },
    delivery: {
        label: 'Delivery & cost',
        description: 'How much exposure the auction bought, and what it cost.',
    },
    clicks: {
        label: 'Clicks & traffic',
        description: 'From on-screen to on-site.',
    },
    funnel: {
        label: 'On-site funnel',
        description: 'Click to outcome, step by step.',
    },
    video: {
        label: 'Video & creative diagnostics',
        description: 'These explain performance — they do not judge it.',
    },
    engagement: {
        label: 'Engagement',
        description: 'Social proof accumulation.',
    },
    leads: {
        label: 'Leads & contact',
        description: 'The outcome group for lead-gen and services accounts.',
    },
    appTrials: {
        label: 'App, trials & subscriptions',
        description: 'The outcome group for SaaS and app accounts.',
    },
    lifecycle: {
        label: 'Lifecycle & testing',
        description: 'Where the creative is in its life.',
    },
};
/** Meta's delivery grades are RELATIVE — the note is the same for all three, so it is named once. */
/**
 * The creative lifecycle vocabulary, shared by the hand-set label and the derived status.
 *
 * Lives HERE rather than in the web table module because both the metric catalog and the frontend's
 * status derivation need it, and importing it from the table created a CYCLE: the table imports the
 * grid metrics, which would import the table back, leaving `GRID_METRIC_BY_KEY` undefined at
 * module-evaluation time. One vocabulary in one place also means the derived status and the manual
 * label cannot drift into two different sets of words.
 */
exports.ANNOTATION_STATUSES = ['WINNER', 'SCALING', 'TESTING', 'LOSER', 'RETIRED'];
exports.ANNOTATION_STATUS_LABEL = {
    WINNER: 'Winner',
    SCALING: 'Scaling',
    TESTING: 'Testing',
    LOSER: 'Loser',
    RETIRED: 'Retired',
};
const RELATIVE_GRADE_NOTE = 'Meta\u2019s own relative grade \u2014 it moves when the competition does, not only when the ad does.';
/** Volume, not efficiency — there is no impression count or spend that is good or bad on its own. */
const VOLUME_NOTE = 'Scale, not efficiency — no good or bad value on its own.';
/** A count is volume: eight conversions is neither good nor bad without a spend beside it. */
const COUNT_NOTE = 'A count, not a rate — judge it against what it cost.';
/**
 * The catalog.
 *
 * Ordered within each group the way a buyer reads it — the outcome first, then what it cost, then
 * the rate that explains it. The picker renders this order; it is not alphabetical, because
 * alphabetical order separates a count from the cost-per that divides it.
 */
exports.CREATIVE_METRIC_CATALOG = [
    // ── Results ──────────────────────────────────────────────────────────────────────────────
    {
        key: 'spend',
        label: 'Spend',
        group: 'results',
        unit: 'currency',
        source: 'column',
        field: 'spendMicros',
        description: 'What the creative cost in this window.',
        summable: true,
        // Budget is an input the buyer chose, not a result the creative produced.
        verdict: { kind: 'none', note: VOLUME_NOTE },
    },
    {
        key: 'purchases',
        label: 'Purchases',
        group: 'results',
        unit: 'count',
        source: 'event',
        field: 'purchaseConversions',
        description: 'The account’s terminal conversion count — the denominator of every cost-per.',
        summable: true,
        requires: ['terminalEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'purchaseValue',
        label: 'Purchase value',
        group: 'results',
        unit: 'currency',
        source: 'column',
        field: 'revenueMicros',
        description: 'The revenue behind the return — shown so nobody has to reverse-engineer it.',
        summable: true,
        requires: ['terminalValue'],
        // A small revenue on a small spend is not a bad creative; the ratio is what judges it.
        verdict: { kind: 'none', note: VOLUME_NOTE },
    },
    {
        key: 'roas',
        label: 'ROAS',
        group: 'results',
        unit: 'multiple',
        source: 'derived',
        formula: 'outcome value ÷ spend',
        description: 'What each unit of spend returned.',
        summable: false,
        requires: ['terminalValue'],
        derivation: { numerator: 'purchaseValue', denominator: 'spend' },
        verdict: { kind: 'band', band: 'roas' },
    },
    {
        key: 'cpa',
        label: 'Cost / purchase',
        group: 'results',
        unit: 'currency',
        source: 'derived',
        formula: 'spend ÷ the account’s terminal conversions',
        field: 'costPerPurchaseMicros',
        description: 'What one outcome cost, whatever the account converts on.',
        summable: false,
        requires: ['terminalEvent'],
        derivation: { numerator: 'spend', denominator: 'purchases' },
        verdict: { kind: 'inheritReturn' },
    },
    {
        key: 'costPerResult',
        label: 'Cost / result',
        group: 'results',
        unit: 'currency',
        source: 'derived',
        formula: 'spend ÷ the ad set’s own optimisation result',
        field: 'costPerResultMicros',
        description: 'Did the creative deliver what it was bought on? Withheld across mixed objectives.',
        summable: false,
        derivation: { numerator: 'spend', denominator: 'results' },
        // A lead, a ThruPlay and a purchase are not on one scale, so no threshold is fair across two
        // creatives in the same grid.
        verdict: { kind: 'none', note: 'Depends on the objective — no comparable threshold across creatives.' },
    },
    {
        key: 'results',
        label: 'Results',
        group: 'results',
        unit: 'count',
        source: 'column',
        description: 'The count the cost-per-result divides by.',
        summable: true,
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'aov',
        label: 'Average order value',
        group: 'results',
        unit: 'currency',
        source: 'derived',
        formula: 'outcome value ÷ outcome count',
        field: 'aovMicros',
        description: 'An AOV spike explains a ROAS spike — basket, not creative.',
        summable: false,
        requires: ['terminalValue'],
        derivation: { numerator: 'purchaseValue', denominator: 'purchases' },
        // Money with no comparable anchor: an AOV is a property of the catalogue, not the creative.
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor — compare it against this account\u2019s own average.' },
    },
    {
        key: 'conversionRate',
        label: 'Conversion rate',
        group: 'results',
        unit: 'rate',
        source: 'derived',
        // The denominator is chosen per account, not fixed: `landing_page_view` comes from the
        // browser pixel and under-counts structurally on a consent-gated site, so a credibility test
        // picks between arrivals and clicks. The row states which one it used.
        formula: 'outcome count \u00f7 the denominator the account measures credibly',
        description: 'The AD-LEVEL pixel rate. Structurally higher than a store CVR, and never comparable to one.',
        summable: false,
        requires: ['terminalEvent'],
        derivation: { numerator: 'purchases', denominator: 'landingPageViews' },
        verdict: { kind: 'none', note: 'No published ad-level band \u2014 a 2% rate is excellent for furniture and poor for a lead magnet.' },
    },
    {
        key: 'revenuePerVisitor',
        label: 'Revenue per visitor',
        group: 'results',
        unit: 'currency',
        source: 'derived',
        formula: 'outcome value \u00f7 landing page views',
        field: 'revenuePerVisitorMicros',
        description: 'Conversion rate and order value collapsed into one per-visitor number.',
        summable: false,
        requires: ['terminalValue', 'landingPageViews'],
        derivation: { numerator: 'purchaseValue', denominator: 'landingPageViews' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own pages.' },
    },
    {
        key: 'cpaVsTarget',
        label: 'CPA vs target',
        group: 'results',
        unit: 'multiple',
        source: 'derived',
        formula: 'cost per outcome \u00f7 the brand\u2019s in-force target',
        description: 'Currency-free, so it is comparable across brands. Withheld when no target was set.',
        summable: false,
        requires: ['terminalEvent', 'brandTarget'],
        derivation: { numerator: 'cpa', denominator: 'cpa' },
        verdict: { kind: 'selfReferential', reference: 'the brand\u2019s CPA target' },
    },
    {
        key: 'spendShareOfAdSet',
        label: 'Spend share of ad set',
        group: 'results',
        unit: 'rate',
        source: 'derived',
        formula: 'spend \u00f7 the combined spend of every ad set this creative ran in',
        description: 'Concentration. One creative past 70% of its ad set is a dependency risk.',
        summable: false,
        derivation: { numerator: 'spend', denominator: 'spend' },
        verdict: { kind: 'none', note: 'Allocation, not performance \u2014 a high share is a risk to notice, not a verdict.' },
    },
    {
        key: 'spendShareOfCampaign',
        label: 'Spend share of campaign',
        group: 'results',
        unit: 'rate',
        source: 'derived',
        formula: 'spend \u00f7 the combined spend of every campaign this creative ran in',
        summable: false,
        derivation: { numerator: 'spend', denominator: 'spend' },
        verdict: { kind: 'none', note: 'Allocation, not performance \u2014 a high share is a risk to notice, not a verdict.' },
    },
    {
        key: 'spendShareOfAccount',
        label: 'Spend share of account',
        group: 'results',
        unit: 'rate',
        source: 'derived',
        formula: 'spend \u00f7 the account\u2019s spend in this window',
        summable: false,
        derivation: { numerator: 'spend', denominator: 'spend' },
        verdict: { kind: 'none', note: 'Allocation, not performance \u2014 a high share is a risk to notice, not a verdict.' },
    },
    // ── Delivery & cost ──────────────────────────────────────────────────────────────────────
    {
        key: 'impressions',
        label: 'Impressions',
        group: 'delivery',
        unit: 'count',
        source: 'column',
        description: 'The denominator of most diagnostics.',
        summable: true,
        verdict: { kind: 'none', note: VOLUME_NOTE },
    },
    {
        key: 'cpm',
        label: 'CPM',
        group: 'delivery',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 impressions \u00d7 1,000',
        field: 'cpmMicros',
        description: 'Rising CPM at flat CTR is auction pressure; rising CPM with falling CTR is fatigue.',
        summable: false,
        derivation: { numerator: 'spend', denominator: 'impressions' },
        verdict: { kind: 'band', band: 'cpm' },
    },
    {
        key: 'frequency',
        label: 'Frequency',
        group: 'delivery',
        unit: 'ratio',
        source: 'derived',
        formula: 'impressions \u00f7 Meta\u2019s window-de-duplicated reach',
        description: 'Reported only where the range-reach lane covers the account, and never from summed daily reach.',
        // Summing daily reach overstates it by a measured 1.99x, which would report frequency at
        // roughly half its real value. There is no de-duplicated reach to fall back to at a grain
        // spanning several ads, so the metric is withheld rather than approximated.
        summable: false,
        requires: ['rangeReach'],
        derivation: { numerator: 'impressions', denominator: 'impressions' },
        // The doctrine's own fatigue section says a stable frequency of 5 is fine and that it is the
        // RISE that matters — which a single row cannot see.
        verdict: { kind: 'none', note: 'A level, not a trend \u2014 the doctrine judges the rise, which one row cannot show.' },
    },
    {
        key: 'deliveryStatus',
        label: 'Delivery',
        group: 'delivery',
        unit: 'status',
        source: 'column',
        field: 'effectiveStatus',
        description: 'Active, paused or in review. A creative\u2019s ads can differ, and the value then says so.',
        summable: false,
        verdict: { kind: 'none', note: 'A provider fact, not a judgement.' },
    },
    // \u2500\u2500 Clicks & traffic \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
    {
        key: 'outboundClicks',
        label: 'Outbound clicks',
        group: 'clicks',
        unit: 'count',
        source: 'column',
        description: 'Clicks that actually left Meta \u2014 the honest traffic number.',
        summable: true,
        requires: ['outboundClicks'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'outboundCtr',
        label: 'Outbound CTR',
        group: 'clicks',
        unit: 'rate',
        source: 'derived',
        formula: 'outbound clicks \u00f7 impressions',
        summable: false,
        requires: ['outboundClicks'],
        derivation: { numerator: 'outboundClicks', denominator: 'impressions' },
        verdict: { kind: 'band', band: 'outboundCtr' },
    },
    {
        key: 'landingPageViews',
        label: 'Landing page views',
        group: 'clicks',
        unit: 'count',
        source: 'event',
        description: 'The anchor of the on-site funnel.',
        summable: true,
        requires: ['landingPageViews'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'landingPageViewRate',
        label: 'Landing page view rate',
        group: 'clicks',
        unit: 'rate',
        source: 'derived',
        formula: 'landing page views \u00f7 outbound clicks',
        description: 'Below the healthy band this is a page-speed or redirect problem, not a creative one.',
        summable: false,
        requires: ['landingPageViews', 'outboundClicks'],
        derivation: { numerator: 'landingPageViews', denominator: 'outboundClicks' },
        verdict: { kind: 'band', band: 'landingPageViewRate' },
    },
    // \u2500\u2500 On-site funnel \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
    {
        key: 'addsToCart',
        label: 'Adds to cart',
        group: 'funnel',
        unit: 'count',
        source: 'event',
        description: 'The first commitment signal.',
        summable: true,
        requires: ['addsToCart'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'addToCartRate',
        label: 'Add-to-cart rate',
        group: 'funnel',
        unit: 'rate',
        source: 'derived',
        formula: 'adds to cart \u00f7 landing page views',
        description: 'The quality of the handoff from creative and offer to the page.',
        summable: false,
        requires: ['addsToCart', 'landingPageViews'],
        derivation: { numerator: 'addsToCart', denominator: 'landingPageViews' },
        verdict: { kind: 'band', band: 'addToCartRate' },
    },
    {
        key: 'costPerAddToCart',
        label: 'Cost / add to cart',
        group: 'funnel',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 adds to cart',
        field: 'costPerAddToCartMicros',
        description: 'An early efficiency read, before purchases accumulate.',
        summable: false,
        requires: ['addsToCart'],
        derivation: { numerator: 'spend', denominator: 'addsToCart' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 judge it against this account\u2019s own carts.' },
    },
    // \u2500\u2500 Video & creative diagnostics \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
    {
        key: 'hookRate',
        label: 'Hook rate',
        group: 'video',
        unit: 'rate',
        source: 'derived',
        // The three-second play is the readable standard, and impressions is the denominator because
        // the question is how much of the DELIVERED audience the creative managed to stop.
        formula: '3-second plays \u00f7 impressions',
        description: 'How much of the delivered audience the creative stopped. Also called thumbstop.',
        summable: false,
        requires: ['video'],
        derivation: { numerator: 'threeSecondPlays', denominator: 'impressions' },
        verdict: { kind: 'band', band: 'hookRate' },
    },
    {
        key: 'threeSecondPlays',
        label: '3-second plays',
        group: 'video',
        unit: 'count',
        source: 'event',
        description: 'The raw input behind hook rate, hold rate and hook-to-click.',
        summable: true,
        requires: ['video'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    // \u2500\u2500 Lifecycle & testing \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
    {
        key: 'daysActive',
        label: 'Days active',
        group: 'lifecycle',
        unit: 'days',
        source: 'derived',
        formula: 'whole days since the earliest member ad launched',
        description: 'Longevity is itself a quality signal \u2014 a creative still running after two weeks earned it.',
        summable: false,
        derivation: { numerator: 'daysActive', denominator: 'daysActive' },
        verdict: { kind: 'none', note: 'Age, not performance \u2014 an old creative is not thereby a good or a tired one.' },
    },
    {
        key: 'adCount',
        label: 'Ads',
        group: 'lifecycle',
        unit: 'count',
        source: 'derived',
        formula: 'the number of ads sharing this creative in the window',
        description: 'One asset duplicated across ad sets is one card, not several.',
        summable: true,
        derivation: { numerator: 'adCount', denominator: 'adCount' },
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'outboundCtrChange',
        label: 'Outbound CTR change',
        group: 'lifecycle',
        unit: 'signedRate',
        source: 'derived',
        formula: 'outbound CTR this window \u00f7 the preceding window \u2212 1',
        description: 'Relative, not absolute: a +0.4pp move is unreadable without its base.',
        summable: false,
        requires: ['priorWindow'],
        derivation: { numerator: 'outboundCtr', denominator: 'outboundCtr' },
        verdict: { kind: 'selfReferential', reference: 'its own previous window' },
    },
    {
        key: 'cpmChange',
        label: 'CPM change',
        group: 'lifecycle',
        unit: 'signedRate',
        source: 'derived',
        formula: 'CPM this window \u00f7 the preceding window \u2212 1',
        summable: false,
        requires: ['priorWindow'],
        derivation: { numerator: 'cpm', denominator: 'cpm' },
        verdict: { kind: 'selfReferential', reference: 'its own previous window' },
    },
    {
        key: 'cpaChange',
        label: 'CPA change',
        group: 'lifecycle',
        unit: 'signedRate',
        source: 'derived',
        formula: 'cost per outcome this window \u00f7 the preceding window \u2212 1',
        summable: false,
        requires: ['terminalEvent', 'priorWindow'],
        derivation: { numerator: 'cpa', denominator: 'cpa' },
        verdict: { kind: 'selfReferential', reference: 'its own previous window' },
    },
    {
        key: 'cpaVsOwnPast',
        label: 'CPA vs own past',
        group: 'lifecycle',
        unit: 'multiple',
        source: 'derived',
        formula: 'cost per outcome this window \u00f7 the preceding window',
        description: 'A plain ratio, because the fatigue rule is stated as \u201c2x its own past\u201d.',
        summable: false,
        requires: ['terminalEvent', 'priorWindow'],
        derivation: { numerator: 'cpa', denominator: 'cpa' },
        verdict: { kind: 'selfReferential', reference: 'its own previous window' },
    },
    {
        key: 'costPerOutboundClick',
        label: 'Cost / outbound click',
        group: 'clicks',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 outbound clicks',
        description: 'The click cost that matters for e-commerce \u2014 clicks that actually left Meta.',
        summable: false,
        requires: ['outboundClicks'],
        derivation: { numerator: 'spend', denominator: 'outboundClicks' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own traffic.' },
    },
    {
        key: 'costPerLandingPageView',
        label: 'Cost / landing page view',
        group: 'clicks',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 landing page views',
        description: 'The truest cost of a visitor \u2014 an arrival, not a click that may never have landed.',
        summable: false,
        requires: ['landingPageViews'],
        derivation: { numerator: 'spend', denominator: 'landingPageViews' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own pages.' },
    },
    {
        key: 'clickToPurchase',
        label: 'Click-to-purchase',
        group: 'funnel',
        unit: 'rate',
        source: 'derived',
        formula: 'outcome count \u00f7 outbound clicks',
        // The single number that separates a creative problem from a landing-page one: a creative
        // that wins the click and loses the sale is not the same failure as one nobody clicks.
        description: 'Separates a creative problem from a landing-page problem.',
        summable: false,
        requires: ['terminalEvent', 'outboundClicks'],
        derivation: { numerator: 'purchases', denominator: 'outboundClicks' },
        verdict: { kind: 'none', note: 'No published ad-level band \u2014 the healthy value is a property of the offer, not the ad.' },
    },
    {
        key: 'cartToPurchase',
        label: 'Cart-to-purchase',
        group: 'funnel',
        unit: 'rate',
        source: 'derived',
        formula: 'outcome count \u00f7 adds to cart',
        description: 'An offer and price-sensitivity read: they wanted it, then did not buy it.',
        summable: false,
        requires: ['terminalEvent', 'addsToCart'],
        derivation: { numerator: 'purchases', denominator: 'addsToCart' },
        verdict: { kind: 'none', note: 'No published band \u2014 basket abandonment is a property of the store, not the creative.' },
    },
    {
        key: 'hookToClick',
        label: 'Hook-to-click',
        group: 'video',
        unit: 'rate',
        source: 'derived',
        formula: 'outbound clicks \u00f7 3-second plays',
        description: 'A high hook with a low hook-to-click is an offer or body problem, not a hook problem.',
        // Video-ONLY, not merely video-carrying: on a mixed-format ad, outbound clicks span every
        // rendition while three-second plays fire only on the video one, so the ratio routinely
        // exceeds 100% and is not a rate at all.
        summable: false,
        requires: ['videoOnly', 'outboundClicks'],
        derivation: { numerator: 'outboundClicks', denominator: 'threeSecondPlays' },
        verdict: { kind: 'band', band: 'hookToClick' },
    },
    {
        key: 'fatigueFlag',
        label: 'Fatigue',
        group: 'lifecycle',
        unit: 'status',
        source: 'derived',
        formula: 'outbound CTR falling AND cost per outcome rising, both against the preceding window',
        // Deliberately NOT gated on rising frequency, which the doctrine's own fatigue call names:
        // frequency needs Meta's window-de-duplicated reach, which covers ~3% of accounts, so
        // requiring it would report "no fatigued creatives" for the other 97% rather than "not
        // computable" \u2014 the same structural blank the cost-per rule already had to route around.
        description: 'A supporting signal, not a diagnosis: the doctrine\u2019s full call needs audience data one row cannot carry.',
        summable: false,
        requires: ['priorWindow'],
        derivation: { numerator: 'outboundCtrChange', denominator: 'cpaVsOwnPast' },
        verdict: { kind: 'none', note: 'A flag, not a grade \u2014 it points at a creative to look at, and looking is the next step.' },
    },
    {
        key: 'videoPlays',
        label: 'Video plays',
        group: 'video',
        unit: 'count',
        source: 'column',
        description: 'The denominator of the retention curve, and the signal that the creative IS a video.',
        summable: true,
        requires: ['video'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'holdRate',
        label: 'Hold rate',
        group: 'video',
        unit: 'rate',
        source: 'derived',
        // ThruPlays over THREE-SECOND PLAYS. Dividing by impressions instead makes it a second hook
        // rate wearing a different label, and understates it by roughly the hook rate itself.
        formula: 'ThruPlays \u00f7 3-second plays',
        description: 'How many of the people it stopped it then kept.',
        summable: false,
        requires: ['video'],
        derivation: { numerator: 'thruPlays', denominator: 'threeSecondPlays' },
        verdict: { kind: 'band', band: 'holdRate' },
    },
    {
        key: 'thruPlays',
        label: 'ThruPlays',
        group: 'video',
        unit: 'count',
        source: 'column',
        description: '15 seconds, or completion for a shorter cut.',
        summable: true,
        requires: ['video'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerThruPlay',
        label: 'Cost / ThruPlay',
        group: 'video',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 ThruPlays',
        field: 'costPerThruPlayMicros',
        summable: false,
        requires: ['video'],
        derivation: { numerator: 'spend', denominator: 'thruPlays' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own video.' },
    },
    {
        key: 'firstFrameRetention',
        label: 'First-frame retention',
        group: 'video',
        unit: 'rate',
        source: 'derived',
        formula: 'video plays \u00f7 impressions',
        description: 'The pre-hook signal: did the video even start in feed.',
        summable: false,
        requires: ['video'],
        derivation: { numerator: 'videoPlays', denominator: 'impressions' },
        verdict: { kind: 'none', note: 'No published band \u2014 autoplay behaviour differs by placement and connection.' },
    },
    {
        key: 'retentionP25',
        label: 'Retention 25%',
        group: 'video',
        unit: 'rate',
        source: 'derived',
        formula: 'plays reaching 25% \u00f7 video plays',
        // Four points, not five: Meta exposes p25/p50/p75/p100 and has no p95, despite the metric
        // sometimes being written that way.
        description: 'The completion curve. The steepest drop between points is the edit to fix.',
        summable: false,
        requires: ['video'],
        derivation: { numerator: 'videoPlays', denominator: 'videoPlays' },
        verdict: { kind: 'none', note: 'Explains performance, does not judge it \u2014 retention varies with length and format.' },
    },
    {
        key: 'retentionP50',
        label: 'Retention 50%',
        group: 'video',
        unit: 'rate',
        source: 'derived',
        formula: 'plays reaching 50% \u00f7 video plays',
        summable: false,
        requires: ['video'],
        derivation: { numerator: 'videoPlays', denominator: 'videoPlays' },
        verdict: { kind: 'none', note: 'Explains performance, does not judge it \u2014 retention varies with length and format.' },
    },
    {
        key: 'retentionP75',
        label: 'Retention 75%',
        group: 'video',
        unit: 'rate',
        source: 'derived',
        formula: 'plays reaching 75% \u00f7 video plays',
        summable: false,
        requires: ['video'],
        derivation: { numerator: 'videoPlays', denominator: 'videoPlays' },
        verdict: { kind: 'none', note: 'Explains performance, does not judge it \u2014 retention varies with length and format.' },
    },
    {
        key: 'retentionP100',
        label: 'Retention 100%',
        group: 'video',
        unit: 'rate',
        source: 'derived',
        formula: 'plays reaching 100% \u00f7 video plays',
        description: 'Completion quality \u2014 most meaningful on a short cut.',
        summable: false,
        requires: ['video'],
        derivation: { numerator: 'videoPlays', denominator: 'videoPlays' },
        verdict: { kind: 'none', note: 'Explains performance, does not judge it \u2014 retention varies with length and format.' },
    },
    {
        key: 'twoSecondPlays',
        label: '2-second continuous plays',
        group: 'video',
        unit: 'count',
        source: 'column',
        // Kept for completeness only: this was the panel's original hook-rate numerator, and it is a
        // weaker, less comparable event than the three-second play.
        description: 'A weaker stop signal than the 3-second play. Never a hook-rate numerator.',
        summable: true,
        requires: ['video'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'clicks',
        label: 'Clicks (all)',
        group: 'clicks',
        unit: 'count',
        source: 'column',
        description: 'Includes profile taps, expands and reactions \u2014 wider than the link families.',
        summable: true,
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'ctr',
        label: 'CTR (all)',
        group: 'clicks',
        unit: 'rate',
        source: 'derived',
        formula: 'all clicks \u00f7 impressions',
        description: 'A vanity duplicate of outbound CTR on an e-commerce account.',
        summable: false,
        derivation: { numerator: 'clicks', denominator: 'impressions' },
        verdict: { kind: 'band', band: 'ctr' },
    },
    {
        key: 'cpc',
        label: 'CPC (all)',
        group: 'clicks',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 all clicks',
        field: 'cpcMicros',
        summable: false,
        derivation: { numerator: 'spend', denominator: 'clicks' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own traffic.' },
    },
    {
        key: 'linkClicks',
        label: 'Link clicks',
        group: 'clicks',
        unit: 'count',
        source: 'column',
        description: 'Slightly wider than outbound \u2014 includes on-Meta destinations.',
        summable: true,
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'linkCtr',
        label: 'Link CTR',
        group: 'clicks',
        unit: 'rate',
        source: 'derived',
        formula: 'link clicks \u00f7 impressions',
        summable: false,
        derivation: { numerator: 'linkClicks', denominator: 'impressions' },
        verdict: { kind: 'none', note: 'The published band is calibrated on OUTBOUND clicks \u2014 this counts a wider set.' },
    },
    {
        key: 'costPerLinkClick',
        label: 'Cost / link click',
        group: 'clicks',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 link clicks',
        field: 'costPerLinkClickMicros',
        summable: false,
        derivation: { numerator: 'spend', denominator: 'linkClicks' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own traffic.' },
    },
    {
        key: 'uniqueClicks',
        label: 'Unique clicks',
        group: 'clicks',
        unit: 'count',
        source: 'column',
        description: 'People-based dedupe \u2014 useful at high frequency.',
        summable: true,
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'initiatedCheckouts',
        label: 'Checkouts initiated',
        group: 'funnel',
        unit: 'count',
        source: 'event',
        summable: true,
        requires: ['initiatedCheckouts'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'checkoutCompletionRate',
        label: 'Checkout completion',
        group: 'funnel',
        unit: 'rate',
        source: 'derived',
        formula: 'outcome count \u00f7 checkouts initiated',
        description: 'A collapse here points at the checkout, not at the creative.',
        summable: false,
        requires: ['terminalEvent', 'initiatedCheckouts'],
        derivation: { numerator: 'purchases', denominator: 'initiatedCheckouts' },
        verdict: { kind: 'band', band: 'checkoutCompletionRate' },
    },
    {
        key: 'postEngagement',
        label: 'Post engagement',
        group: 'engagement',
        unit: 'count',
        source: 'column',
        description: 'The umbrella count: reactions, comments, shares, saves and clicks together.',
        summable: true,
        requires: ['postEngagement'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'engagementRate',
        label: 'Engagement rate',
        group: 'engagement',
        unit: 'rate',
        source: 'derived',
        formula: 'post engagement \u00f7 impressions',
        description: 'Matters for whitelisting, organic-feel ads and brand accounts.',
        summable: false,
        requires: ['postEngagement'],
        derivation: { numerator: 'postEngagement', denominator: 'impressions' },
        verdict: { kind: 'none', note: 'No published band \u2014 the healthy value depends on format and objective.' },
    },
    {
        key: 'costPerEngagement',
        label: 'Cost / engagement',
        group: 'engagement',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 post engagement',
        field: 'costPerEngagementMicros',
        summable: false,
        requires: ['postEngagement'],
        derivation: { numerator: 'spend', denominator: 'postEngagement' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own posts.' },
    },
    {
        key: 'contentViews',
        label: 'Content views',
        group: 'funnel',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.contentViews.count',
        description: 'Product-page depth beyond the landing page.',
        summable: true,
        requires: ['contentViewsEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerContentViews',
        label: 'Cost / content view',
        group: 'funnel',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 content views',
        field: 'eventMetrics.contentViews.costMicros',
        summable: false,
        requires: ['contentViewsEvent'],
        derivation: { numerator: 'spend', denominator: 'contentViews' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'searches',
        label: 'Searches',
        group: 'funnel',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.searches.count',
        description: 'On-site search — a strong intent signal for large catalogues.',
        summable: true,
        requires: ['searchesEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerSearches',
        label: 'Cost / search',
        group: 'funnel',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 searches',
        field: 'eventMetrics.searches.costMicros',
        summable: false,
        requires: ['searchesEvent'],
        derivation: { numerator: 'spend', denominator: 'searches' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'addsToWishlist',
        label: 'Adds to wishlist',
        group: 'funnel',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.addsToWishlist.count',
        description: 'Relevant for fashion and high-consideration categories.',
        summable: true,
        requires: ['addsToWishlistEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerAddsToWishlist',
        label: 'Cost / wishlist add',
        group: 'funnel',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 adds to wishlist',
        field: 'eventMetrics.addsToWishlist.costMicros',
        summable: false,
        requires: ['addsToWishlistEvent'],
        derivation: { numerator: 'spend', denominator: 'addsToWishlist' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'addsOfPaymentInfo',
        label: 'Adds of payment info',
        group: 'funnel',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.addsOfPaymentInfo.count',
        description: 'Fine-grained checkout diagnosis.',
        summable: true,
        requires: ['addsOfPaymentInfoEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerAddsOfPaymentInfo',
        label: 'Cost / payment info',
        group: 'funnel',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 adds of payment info',
        field: 'eventMetrics.addsOfPaymentInfo.costMicros',
        summable: false,
        requires: ['addsOfPaymentInfoEvent'],
        derivation: { numerator: 'spend', denominator: 'addsOfPaymentInfo' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'registrations',
        label: 'Registrations',
        group: 'leads',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.registrations.count',
        description: 'Also the top of the SaaS signup funnel.',
        summable: true,
        requires: ['registrationsEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerRegistrations',
        label: 'Cost / registration',
        group: 'leads',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 registrations',
        field: 'eventMetrics.registrations.costMicros',
        summable: false,
        requires: ['registrationsEvent'],
        derivation: { numerator: 'spend', denominator: 'registrations' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'leads',
        label: 'Leads',
        group: 'leads',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.leads.count',
        description: 'The pixel lead event. Instant-form leads are counted separately by Meta.',
        summable: true,
        requires: ['leadsEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerLeads',
        label: 'Cost / lead',
        group: 'leads',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 leads',
        field: 'eventMetrics.leads.costMicros',
        summable: false,
        requires: ['leadsEvent'],
        derivation: { numerator: 'spend', denominator: 'leads' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'contacts',
        label: 'Contacts',
        group: 'leads',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.contacts.count',
        summable: true,
        requires: ['contactsEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerContacts',
        label: 'Cost / contact',
        group: 'leads',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 contacts',
        field: 'eventMetrics.contacts.costMicros',
        summable: false,
        requires: ['contactsEvent'],
        derivation: { numerator: 'spend', denominator: 'contacts' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'schedules',
        label: 'Schedules',
        group: 'leads',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.schedules.count',
        description: 'Appointment businesses.',
        summable: true,
        requires: ['schedulesEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerSchedules',
        label: 'Cost / schedule',
        group: 'leads',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 schedules',
        field: 'eventMetrics.schedules.costMicros',
        summable: false,
        requires: ['schedulesEvent'],
        derivation: { numerator: 'spend', denominator: 'schedules' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'applications',
        label: 'Applications',
        group: 'leads',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.applications.count',
        summable: true,
        requires: ['applicationsEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerApplications',
        label: 'Cost / application',
        group: 'leads',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 applications',
        field: 'eventMetrics.applications.costMicros',
        summable: false,
        requires: ['applicationsEvent'],
        derivation: { numerator: 'spend', denominator: 'applications' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'findLocation',
        label: 'Location finds',
        group: 'leads',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.findLocation.count',
        description: 'Local businesses.',
        summable: true,
        requires: ['findLocationEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerFindLocation',
        label: 'Cost / location find',
        group: 'leads',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 location finds',
        field: 'eventMetrics.findLocation.costMicros',
        summable: false,
        requires: ['findLocationEvent'],
        derivation: { numerator: 'spend', denominator: 'findLocation' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'messagingConversations',
        label: 'Messaging conversations',
        group: 'leads',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.messagingConversations.count',
        description: 'WhatsApp and Messenger-first businesses.',
        summable: true,
        requires: ['messagingConversationsEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerMessagingConversations',
        label: 'Cost / conversation',
        group: 'leads',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 messaging conversations',
        field: 'eventMetrics.messagingConversations.costMicros',
        summable: false,
        requires: ['messagingConversationsEvent'],
        derivation: { numerator: 'spend', denominator: 'messagingConversations' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'calls',
        label: 'Calls',
        group: 'leads',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.calls.count',
        description: 'Placed calls, not taps.',
        summable: true,
        requires: ['callsEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerCalls',
        label: 'Cost / call',
        group: 'leads',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 calls',
        field: 'eventMetrics.calls.costMicros',
        summable: false,
        requires: ['callsEvent'],
        derivation: { numerator: 'spend', denominator: 'calls' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'trialsStarted',
        label: 'Trials started',
        group: 'appTrials',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.trialsStarted.count',
        description: 'The SaaS equivalent of a purchase.',
        summable: true,
        requires: ['trialsStartedEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerTrialsStarted',
        label: 'Cost / trial',
        group: 'appTrials',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 trials started',
        field: 'eventMetrics.trialsStarted.costMicros',
        summable: false,
        requires: ['trialsStartedEvent'],
        derivation: { numerator: 'spend', denominator: 'trialsStarted' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'subscriptions',
        label: 'Subscriptions',
        group: 'appTrials',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.subscriptions.count',
        description: 'Pair with trials for the same-row funnel read.',
        summable: true,
        requires: ['subscriptionsEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerSubscriptions',
        label: 'Cost / subscription',
        group: 'appTrials',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 subscriptions',
        field: 'eventMetrics.subscriptions.costMicros',
        summable: false,
        requires: ['subscriptionsEvent'],
        derivation: { numerator: 'spend', denominator: 'subscriptions' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'appInstalls',
        label: 'App installs',
        group: 'appTrials',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.appInstalls.count',
        summable: true,
        requires: ['appInstallsEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerAppInstalls',
        label: 'Cost / install',
        group: 'appTrials',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 app installs',
        field: 'eventMetrics.appInstalls.costMicros',
        summable: false,
        requires: ['appInstallsEvent'],
        derivation: { numerator: 'spend', denominator: 'appInstalls' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'trialToSubscriptionRate',
        label: 'Trial → subscription',
        group: 'appTrials',
        unit: 'rate',
        source: 'derived',
        formula: 'subscriptions \u00f7 trials started',
        // Attribution-window caveat: a trial converts later than the window that recorded it, so
        // this reads low on a short range and the surface should say so.
        description: 'Trials convert after the window that recorded them — read it on a long range.',
        summable: false,
        requires: ['trialsStartedEvent', 'subscriptionsEvent'],
        derivation: { numerator: 'subscriptions', denominator: 'trialsStarted' },
        verdict: { kind: 'none', note: 'No published band \u2014 healthy trial conversion is a property of the product.' },
    },
    {
        key: 'averagePlayTime',
        label: 'Average play time',
        group: 'video',
        unit: 'seconds',
        source: 'derived',
        formula: 'total seconds watched \u00f7 video plays',
        field: 'averagePlayTimeSeconds',
        // The asset's own length is not on the row, so this cannot be shown as "% watched" here.
        description: 'Cross-check against the asset\u2019s length to read it as a share watched.',
        summable: false,
        requires: ['video'],
        derivation: { numerator: 'videoPlays', denominator: 'videoPlays' },
        verdict: { kind: 'none', note: 'Meaningless without the asset\u2019s length \u2014 a 6s average is strong on a 7s cut.' },
    },
    {
        key: 'launchDate',
        label: 'Launch date',
        group: 'lifecycle',
        unit: 'date',
        source: 'column',
        field: 'launchedAt',
        description: 'The EARLIEST member ad\u2019s launch — a later-added ad does not restart the clock.',
        summable: false,
        verdict: { kind: 'none', note: 'A date, not a measurement.' },
    },
    {
        key: 'qualityRanking',
        label: 'Quality ranking',
        group: 'delivery',
        unit: 'status',
        source: 'column',
        field: 'rankings.qualityRanking',
        // Meta's own scale: "average" is the 35th–55th percentile against ads competing for the
        // SAME audience — a relative grade, not an absolute one, so it moves when the competition does.
        description: 'Above / average / below, against ads competing for the same audience.',
        summable: false,
        verdict: { kind: 'none', note: RELATIVE_GRADE_NOTE },
    },
    {
        key: 'engagementRateRanking',
        label: 'Engagement rate ranking',
        group: 'delivery',
        unit: 'status',
        source: 'column',
        field: 'rankings.engagementRateRanking',
        description: 'Same scale as quality ranking. Useful as a trio with the other two.',
        summable: false,
        verdict: { kind: 'none', note: RELATIVE_GRADE_NOTE },
    },
    {
        key: 'conversionRateRanking',
        label: 'Conversion rate ranking',
        group: 'delivery',
        unit: 'status',
        source: 'column',
        field: 'rankings.conversionRateRanking',
        description: 'Same scale, compared against ads with the same optimisation goal.',
        summable: false,
        verdict: { kind: 'none', note: RELATIVE_GRADE_NOTE },
    },
    {
        key: 'attributionSetting',
        label: 'Attribution setting',
        group: 'delivery',
        unit: 'status',
        source: 'column',
        field: 'rankings.attributionSetting',
        // Context for every conversion number beside it: two creatives measured on different windows
        // are not comparable, and nothing else on the row says so.
        description: 'The window every conversion figure on this row was measured under.',
        summable: false,
        verdict: { kind: 'none', note: 'Context for the conversion numbers, not a measurement of the creative.' },
    },
    {
        key: 'clickThroughConversions',
        label: 'Click-through conversions',
        group: 'delivery',
        unit: 'count',
        source: 'column',
        // Readable directly rather than as `total \u2212 view`: a reader should not have to do
        // arithmetic to learn how many of the outcomes were actually clicked.
        description: 'Conversions the platform credited to a CLICK, not merely to an impression.',
        summable: true,
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'viewThroughConversions',
        label: 'View-through conversions',
        group: 'delivery',
        unit: 'count',
        source: 'column',
        description: 'Conversions credited because the ad was SEEN, with no click. The share\u2019s numerator.',
        summable: true,
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'viewThroughShare',
        label: 'View-through share',
        group: 'delivery',
        unit: 'rate',
        source: 'derived',
        formula: 'View-through conversions \u00f7 Results',
        // The first metric on this surface whose subject is how much to believe the OTHERS. It is
        // a property of the account's measurement and its audience, not of the creative's quality
        // \u2014 which is why the band is the only verdict it carries and why nothing here calls a
        // high share bad.
        description: 'How much of this creative\u2019s credit came from views rather than clicks.',
        summable: false,
        derivation: { numerator: 'viewThroughConversions', denominator: 'results' },
        verdict: { kind: 'band', band: 'viewThroughShare' },
    },
    {
        key: 'uniqueOutboundClicks',
        label: 'Unique outbound clicks',
        group: 'clicks',
        unit: 'count',
        source: 'column',
        description: 'People who left Meta, not clicks. A multi-day figure is an upper bound.',
        summable: true,
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'uniqueLinkClicks',
        label: 'Unique link clicks',
        group: 'clicks',
        unit: 'count',
        source: 'column',
        description: 'People who clicked a link, including on-Meta destinations. An upper bound over days.',
        summable: true,
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'thirtySecondViews',
        label: '30-second views',
        group: 'video',
        unit: 'count',
        source: 'column',
        description: 'A longer hold than ThruPlay\u2019s 15 seconds, on any creative over 30 seconds.',
        summable: true,
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'reactions',
        label: 'Reactions',
        group: 'engagement',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.reactions.count',
        description: 'Likes, loves and the rest on the ad\u2019s own post.',
        summable: true,
        requires: ['reactionsEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerReactions',
        label: 'Cost / reaction',
        group: 'engagement',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 reactions',
        field: 'eventMetrics.reactions.costMicros',
        summable: false,
        requires: ['reactionsEvent'],
        derivation: { numerator: 'spend', denominator: 'reactions' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'comments',
        label: 'Comments',
        group: 'engagement',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.comments.count',
        description: 'Comments on the post. High volume can be complaints \u2014 read them.',
        summable: true,
        requires: ['commentsEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerComments',
        label: 'Cost / comment',
        group: 'engagement',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 comments',
        field: 'eventMetrics.comments.costMicros',
        summable: false,
        requires: ['commentsEvent'],
        derivation: { numerator: 'spend', denominator: 'comments' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'shares',
        label: 'Shares',
        group: 'engagement',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.shares.count',
        description: 'The one engagement that earns distribution the spend did not buy.',
        summable: true,
        requires: ['sharesEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerShares',
        label: 'Cost / share',
        group: 'engagement',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 shares',
        field: 'eventMetrics.shares.costMicros',
        summable: false,
        requires: ['sharesEvent'],
        derivation: { numerator: 'spend', denominator: 'shares' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'saves',
        label: 'Saves',
        group: 'engagement',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.saves.count',
        description: 'Intent to return \u2014 a stronger signal than a reaction.',
        summable: true,
        requires: ['savesEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerSaves',
        label: 'Cost / save',
        group: 'engagement',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 saves',
        field: 'eventMetrics.saves.costMicros',
        summable: false,
        requires: ['savesEvent'],
        derivation: { numerator: 'spend', denominator: 'saves' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'pageLikes',
        label: 'Page likes',
        group: 'engagement',
        unit: 'count',
        source: 'event',
        field: 'eventMetrics.pageLikes.count',
        description: 'Follows earned by the ad, not by the page.',
        summable: true,
        requires: ['pageLikesEvent'],
        verdict: { kind: 'none', note: COUNT_NOTE },
    },
    {
        key: 'costPerPageLikes',
        label: 'Cost / page like',
        group: 'engagement',
        unit: 'currency',
        source: 'derived',
        formula: 'spend \u00f7 page likes',
        field: 'eventMetrics.pageLikes.costMicros',
        summable: false,
        requires: ['pageLikesEvent'],
        derivation: { numerator: 'spend', denominator: 'pageLikes' },
        verdict: { kind: 'none', note: 'Money with no cross-brand anchor \u2014 read it against this account\u2019s own history.' },
    },
    {
        key: 'spendChange',
        label: 'Spend change',
        group: 'lifecycle',
        unit: 'signedRate',
        source: 'derived',
        formula: 'spend this window \u00f7 the preceding window \u2212 1',
        description: 'Whether the creative is being scaled into or away from.',
        summable: false,
        requires: ['priorWindow'],
        derivation: { numerator: 'spend', denominator: 'spend' },
        verdict: { kind: 'selfReferential', reference: 'its own previous window' },
    },
    {
        key: 'roasChange',
        label: 'ROAS change',
        group: 'lifecycle',
        unit: 'signedRate',
        source: 'derived',
        formula: 'ROAS this window \u00f7 the preceding window \u2212 1',
        description: 'The return moving, not its level \u2014 read beside the spend change.',
        summable: false,
        requires: ['priorWindow', 'terminalValue'],
        derivation: { numerator: 'roas', denominator: 'roas' },
        verdict: { kind: 'selfReferential', reference: 'its own previous window' },
    },
    {
        key: 'hookRateChange',
        label: 'Hook rate change',
        group: 'lifecycle',
        unit: 'signedRate',
        source: 'derived',
        formula: 'hook rate this window \u00f7 the preceding window \u2212 1',
        description: 'The earliest fatigue signal \u2014 the opening stops working before the cost moves.',
        summable: false,
        requires: ['priorWindow', 'video'],
        derivation: { numerator: 'hookRate', denominator: 'hookRate' },
        verdict: { kind: 'selfReferential', reference: 'its own previous window' },
    },
    {
        key: 'derivedStatus',
        // NOT "Status": the table already has a hand-set `annotationStatus` column under that exact
        // label, and two columns called Status — one a person's label, one a rule's — is the single
        // most confusable pair this surface could offer. Seen side by side in the picker during
        // browser verification.
        label: 'Auto status',
        group: 'lifecycle',
        unit: 'status',
        source: 'derived',
        formula: 'cost per outcome against the brand\u2019s target, gated on outcome volume',
        // The hand-set label already exists and already carries this vocabulary; on a fleet where
        // almost nobody labels, it is empty for almost every creative, which teaches a reader to
        // ignore the column. A hand-set value always WINS here \u2014 a person who opened the creative
        // knows things the rule cannot see.
        description: 'Derived from the numbers, and overridden by a label a person set.',
        summable: false,
        requires: ['brandTarget'],
        verdict: { kind: 'none', note: 'A rule, not a judgement \u2014 withheld entirely below the volume floor and where the brand set no target.' },
    },
];
/**
 * The catalog as the ENTRY INTERFACE, in catalog order.
 *
 * `CREATIVE_METRIC_CATALOG` is `as const`, which is what recovers the key literals — but it also
 * narrows each entry to its exact written shape, so a property some entries omit (`formula`,
 * `derivation`, `requires`) does not exist on the union at all. Anything iterating the catalog to
 * read an OPTIONAL property wants this widened view; anything that needs the key union wants the
 * const one.
 */
exports.CREATIVE_METRIC_ENTRIES = exports.CREATIVE_METRIC_CATALOG;
/** Whether a metric's value is a number, and so may carry a filter bound. */
function isNumericCreativeMetric(metric) {
    return metric.unit !== 'status' && metric.unit !== 'date';
}
/** Every catalog key, in catalog order. */
exports.CREATIVE_METRIC_KEYS = exports.CREATIVE_METRIC_CATALOG.map((m) => m.key);
exports.CREATIVE_METRIC_BY_KEY = Object.fromEntries(exports.CREATIVE_METRIC_CATALOG.map((m) => [m.key, m]));
/** The catalog's entries for one group, in catalog order. */
function creativeMetricsInGroup(group) {
    return exports.CREATIVE_METRIC_CATALOG.filter((m) => m.group === group);
}
/**
 * The entry for a key, or `undefined` when the key names nothing.
 *
 * Callers that hold a key from the catalog itself should index `CREATIVE_METRIC_BY_KEY` directly;
 * this exists for the boundaries where a key arrives from a URL or a persisted view and may be one
 * the catalog no longer declares.
 */
function creativeMetric(key) {
    return exports.CREATIVE_METRIC_BY_KEY[key];
}
/** Whether a string names a metric the catalog still declares — the guard for a URL or saved view. */
function isCreativeMetricKey(key) {
    return key in exports.CREATIVE_METRIC_BY_KEY;
}
